import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRp } from "@/lib/api";
import { Check, Server, MapPin, Box, Loader2, ChevronRight, ChevronLeft, KeyRound, Copy, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const steps = ["Plan", "Egg", "Lokasi", "Konfirmasi"];

export default function CreateServerWizard({ open, onOpenChange, plans, eggs, locations, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState(null);
  const [egg, setEgg] = useState(null);
  const [loc, setLoc] = useState(null);
  const [name, setName] = useState("");
  const [pwPreview, setPwPreview] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(0); setPlan(null); setEgg(null); setLoc(null); setName("");
  };

  // Password stabil: pakai password panel user yang sudah ada; hanya berubah kalau user klik generate
  useEffect(() => {
    if (!open) return;
    if (user?.ptero_password) setPwPreview(user.ptero_password);
    else if (!pwPreview) genPw();
  }, [open, user, pwPreview]);

  const genPw = async () => {
    const { data } = await api.get("/utils/generate-password?length=16");
    setPwPreview(data.password);
  };

  const next = () => {
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Masukkan nama server");
    setLoading(true);
    try {
      const { data } = await api.post("/servers", {
        name, plan_id: plan.id, egg_id: egg.id, location_id: loc.id,
      });
      if (data.status === "failed") {
        toast.error("Server dibuat tapi gagal deploy: " + (data.error || "unknown"));
      } else {
        toast.success("Server berhasil dibuat!");
      }
      onCreated?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat server");
    } finally {
      setLoading(false);
    }
  };

  const canNext = (step === 0 && plan) || (step === 1 && egg) || (step === 2 && loc);
  const enabledPlans = plans.filter((p) => p.enabled);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="glass-strong border-cyan-500/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-server-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Server className="text-cyan-400" /> Buat Server Baru
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-2">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                ${i <= step ? "btn-cyan text-white" : "bg-slate-800 text-slate-500"}`}>
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-sm ${i <= step ? "text-cyan-300" : "text-slate-500"}`}>{s}</span>
              {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-cyan-500/50" : "bg-slate-800"}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[300px] py-2">
          {step === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wizard-plans">
              {enabledPlans.map((p) => (
                <button key={p.id} onClick={() => setPlan(p)}
                  data-testid={`plan-${p.ram_gb}gb-card`}
                  className={`relative p-4 rounded-xl text-left border transition-all glow-hover
                    ${plan?.id === p.id ? "border-cyan-400 bg-cyan-950/40" : "border-slate-700/50 bg-slate-900/40"}`}>
                  {p.is_free && (
                    <span className="absolute -top-2 -right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-black">FREE</span>
                  )}
                  <div className="text-2xl font-heading font-extrabold text-white">{p.ram_gb}<span className="text-sm text-slate-400">GB</span></div>
                  <div className="text-xs text-slate-400 mt-1">{p.vcore} vCore · {p.disk_gb}GB SSD</div>
                  <div className={`mt-2 font-semibold ${p.is_free ? "text-amber-400" : "text-cyan-300"}`}>
                    {p.is_free ? "Gratis" : formatRp(p.price) + "/bln"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="wizard-eggs">
              {eggs.filter((e) => e.enabled).map((e) => (
                <button key={e.id} onClick={() => setEgg(e)}
                  data-testid={`egg-${e.egg_id}-card`}
                  className={`p-4 rounded-xl text-left border transition-all glow-hover flex items-start gap-3
                    ${egg?.id === e.id ? "border-cyan-400 bg-cyan-950/40" : "border-slate-700/50 bg-slate-900/40"}`}>
                  <Box className="text-cyan-400 mt-0.5 shrink-0" size={20} />
                  <div>
                    <div className="font-semibold text-white text-sm">{e.name}</div>
                    <div className="text-xs text-slate-500 font-mono mt-1">Egg ID {e.egg_id}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="wizard-locations">
              {locations.filter((l) => l.enabled).map((l) => (
                <button key={l.id} onClick={() => setLoc(l)}
                  data-testid={`location-${l.location_id}-card`}
                  className={`p-5 rounded-xl text-left border transition-all glow-hover
                    ${loc?.id === l.id ? "border-cyan-400 bg-cyan-950/40" : "border-slate-700/50 bg-slate-900/40"}`}>
                  <MapPin className="text-cyan-400 mb-2" size={22} />
                  <div className="font-heading font-bold text-white text-lg">{l.code}</div>
                  <div className="text-xs text-slate-400">{l.name}</div>
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-confirm">
              <div>
                <Label className="text-slate-300">Nama Server</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Server Minecraft Ku"
                  className="mt-1 bg-slate-900/60 border-cyan-500/20 text-white" data-testid="server-name-input" />
              </div>
              <div className="p-4 rounded-xl bg-slate-950/60 border border-cyan-500/20">
                <div className="flex items-center justify-between text-xs uppercase tracking-wider text-cyan-400 mb-2">
                  <span><KeyRound size={12} className="inline mr-1" /> Password Pterodactyl</span>
                  <div className="flex items-center gap-3">
                    <button onClick={genPw} className="hover:text-white" title="Generate password baru" data-testid="wizard-regen-pw"><RefreshCw size={14} /></button>
                    <button onClick={() => { navigator.clipboard.writeText(pwPreview); toast.success("Disalin"); }}
                      className="hover:text-white" data-testid="wizard-copy-pw"><Copy size={14} /></button>
                  </div>
                </div>
                <div className="font-mono text-cyan-300 break-all text-sm">{pwPreview}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-900/50"><div className="text-slate-500 text-xs">Plan</div><div className="text-white font-semibold">{plan?.ram_gb}GB</div></div>
                <div className="p-3 rounded-lg bg-slate-900/50"><div className="text-slate-500 text-xs">Egg</div><div className="text-white font-semibold text-xs">{egg?.name}</div></div>
                <div className="p-3 rounded-lg bg-slate-900/50"><div className="text-slate-500 text-xs">Lokasi</div><div className="text-white font-semibold">{loc?.code}</div></div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-cyan-950/30 border border-cyan-500/20">
                <span className="text-slate-300">Biaya</span>
                <span className={`font-heading font-bold text-lg ${plan?.is_free ? "text-amber-400" : "text-cyan-300"}`}>
                  {plan?.is_free ? "GRATIS" : formatRp(plan?.price)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex justify-between pt-2 border-t border-slate-800">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0} className="text-slate-400 hover:text-white" data-testid="wizard-back-btn">
            <ChevronLeft size={16} className="mr-1" /> Kembali
          </Button>
          {step < 3 ? (
            <Button onClick={next} disabled={!canNext} className="btn-cyan text-white border-0" data-testid="wizard-next-btn">
              Lanjut <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={loading} className="btn-cyan text-white border-0" data-testid="wizard-deploy-btn">
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Server size={16} className="mr-2" />}
              Deploy Server
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
